// EnumOptionsField.jsx

import React, { useCallback, useState } from 'react';

// Context
import { useIcons } from 'context/IconsContext';

// CSS
import './EnumOptionsField.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';
import ColorButton from 'components/colorbutton/ColorButton';
import IconPickerButton from 'components/iconpicker/IconPickerButton';
import ModalField from 'components/modal/field/ModalField';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import TextInput from 'components/textinput/TextInput';

const resolveOptionIconKey = (iconKey, iconsByKey, fallbackIconKey = '') => {
    const normalizedIconKey = typeof iconKey === 'string' ? iconKey.trim() : '';
    if (!normalizedIconKey) return fallbackIconKey;
    if (!iconsByKey) return normalizedIconKey;
    return iconsByKey[normalizedIconKey] ? normalizedIconKey : fallbackIconKey;
};

/**
 * EnumOptionsField
 *
 * @returns {ReactNode} The rendered component.
 */
const EnumOptionsField = ({
    addLabel = 'Add Option',
    emptyCopy = 'No options yet.',
    label = 'Options',
    selectedEnumOptions,
    optionValueDrafts,
    setOptionValueDrafts,
    onOptionValueCommit,
    onOptionValueKeyDown,
    onOptionColorChange,
    onOptionIconChange,
    onOptionMove,
    onOptionRemove,
    onOptionAdd,
    placeholder = 'Internal value',
    showColors = true,
    showIcons = true,
    theme,
    optionsError,
}) => {
    // ----- CONTEXT ----- //

    const { iconsByKey } = useIcons();

    // ----- STATE ----- //

    const [draggingOptionId, setDraggingOptionId] = useState('');
    const [dragOverOptionId, setDragOverOptionId] = useState('');

    // ----- MEMOS ----- //

    const resetDragState = useCallback(() => {
        setDraggingOptionId('');
        setDragOverOptionId('');
    }, []);

    // ----- HANDLERS ----- //

    /**
 * Handles drag start.
 *
 * @param {*} event
 * @param {*} optionId
 */

    const handleDragStart = useCallback((event, optionId) => {
        if (!optionId) return;
        setDraggingOptionId(optionId);
        setDragOverOptionId(optionId);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', optionId);
    }, []);

    /**
 * Handles drag over.
 *
 * @param {*} event
 * @param {*} optionId
 */
    const handleDragOver = useCallback((event, optionId) => {
        event.preventDefault();
        if (!optionId) return;
        event.dataTransfer.dropEffect = 'move';
        if (dragOverOptionId !== optionId) {
            setDragOverOptionId(optionId);
        }
    }, [dragOverOptionId]);

    /**
 * Handles drop.
 *
 * @param {*} event
 * @param {*} dropOptionId
 */
    const handleDrop = useCallback((event, dropOptionId) => {
        event.preventDefault();

        const transferredId = event.dataTransfer.getData('text/plain');
        const fromOptionId = String(transferredId || draggingOptionId || '').trim();
        const toOptionId = String(dropOptionId || '').trim();

        if (fromOptionId && toOptionId && fromOptionId !== toOptionId) {
            onOptionMove(fromOptionId, toOptionId);
        }

        resetDragState();
    }, [draggingOptionId, onOptionMove, resetDragState]);
    // ----- RENDER ----- //

    return (

        <ModalField>

            <ModalLabel label={label} />
            <ModalHint hint="List of choices for the field." />

            {selectedEnumOptions.length === 0 ? (
                <div
                  className="field-modal-empty"
                  style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    {emptyCopy}
                </div>
            ) : (
                <div className="field-modal-options">
                    {selectedEnumOptions.map((option) => {
                        const optionId = option?.id || option?.value || '';
                        const optionValue = option?.value ?? '';
                        const hasDraft = optionValueDrafts
                            && Object.prototype.hasOwnProperty.call(optionValueDrafts, optionId);
                        const draftValue = hasDraft ? optionValueDrafts[optionId] : optionValue;
                        const pickerIconKey = resolveOptionIconKey(option?.iconKey, iconsByKey, 'category');

                        return (
                            <div
                              className={`field-modal-option${draggingOptionId === optionId ? ' is-dragging' : ''}${dragOverOptionId === optionId && draggingOptionId !== optionId ? ' is-drop-target' : ''}`}
                              key={optionId || optionValue}
                              onDragOver={(event) => handleDragOver(event, optionId)}
                              onDrop={(event) => handleDrop(event, optionId)}
                            >

                                {/* OPTION HANDLE */}
                                <div
                                  className="field-modal-option-handle"
                                  draggable
                                  title="Drag to reorder"
                                  onDragEnd={resetDragState}
                                  onDragStart={(event) => handleDragStart(event, optionId)}
                                >
                                    <Icon
                                      iconKey="drag_indicator"
                                      width={18}
                                      height={18}
                                      color={theme.foregroundMuted}
                                    />
                                </div>

                                {/* OPTION ICON TRIGGER */}
                                {showIcons && typeof onOptionIconChange === 'function' ? (
                                    <IconPickerButton
                                      className="field-modal-option-icon-trigger"
                                      backgroundColor={theme.backgroundMuted}
                                      borderRadius={8}
                                      buttonSize={30}
                                      hoverBackgroundColor={theme.backgroundHover}
                                      iconColor={theme.foregroundMuted}
                                      iconSize={18}
                                      popupHeight={360}
                                      popupWidth={360}
                                      selectedIconKey={pickerIconKey}
                                      title="Choose option icon"
                                      onClear={({ closePicker } = {}) => {
                                            onOptionIconChange(optionId, '');
                                            closePicker?.();
                                        }}
                                      onSelect={(nextIconKey, { closePicker } = {}) => {
                                            onOptionIconChange(optionId, nextIconKey);
                                            closePicker?.();
                                        }}
                                    />
                                ) : null}
                                <TextInput
                                  value={draftValue}
                                  placeholder={placeholder}
                                  onBlur={() => onOptionValueCommit(optionId)}
                                  onChange={(e) => {
                                        const nextValue = e.target.value;
                                        setOptionValueDrafts((prev) => ({
                                            ...(prev || {}),
                                            [optionId]: nextValue,
                                        }));
                                    }}
                                  onKeyDown={(e) => onOptionValueKeyDown(optionId, e)}
                                />
                                {showColors ? (
                                    <ColorButton
                                      color={option?.color}
                                      onChange={(nextColor) => onOptionColorChange(optionId, nextColor)}
                                    />
                                ) : null}

                                {/* OPTION REMOVE */}
                                <div
                                  className="field-modal-option-remove"
                                  onClick={() => onOptionRemove(optionId)}
                                >
                                    <Icon
                                      iconKey="do_not_disturb_on"
                                      width={20}
                                      height={20}
                                      color={theme.foregroundMuted}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {optionsError && (
                <div className="field-modal-error">{optionsError}</div>
            )}

            {/* OPTIONS TOOLBAR */}
            <div className="field-modal-options-toolbar">
                <Button
                  label={addLabel}
                  height="38px"
                  borderRadius="9px"
                  enabled
                  onClick={onOptionAdd}
                />
            </div>

        </ModalField>

    );
};

// Dev Tools
EnumOptionsField.displayName = 'EnumOptionsField';

export default EnumOptionsField;
